import React, { useEffect, useMemo, useContext, useState, useRef } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useLocation } from 'react-router'
import qs from 'query-string'
import { Link } from 'react-router-dom'
import { disciplines, projectTypeMap, programToImage, programColour } from '../../../constant'
import { useLazyQuery } from '@apollo/client'
import { QUERY } from '../../../graphql'
import take from 'lodash.take'
import shuffle from 'lodash.shuffle'
import { GradCard } from '../../../components/2025/GradCard'
import { SpringContext } from '../../../context/SpringContext'
import { ThemeContext } from '../../../context/2025/ThemeContext'
import { useViewport } from '../../../hook/useViewport'

const Header = React.lazy(() => import('../../../components/2025/Header'))
const Footer = React.lazy(() => import('../../../components/2025/Footer'))
const FullScreenLoader = React.lazy(() => import('../../../components/FullScreenLoader'))

export default function Profile(props) {
  const { search } = useLocation()
  const { id } = qs.parse(search)
  const { isDarkMode } = useContext(ThemeContext)
  const { width } = useViewport()
  const isMobile = width <= 640
  const imageRef = useRef(null)

  const { data: studentData, loading: studentLoading } = useQuery(SINGLE_USER_QUERY, {
    variables: {
      id,
    },
  })

  const user = useMemo(() => studentData?.student || {}, [studentData])
  const userId = props.id || user?.id

  const [userImage, setUserImage] = useState('static/2025/phProfile.png')
  const spring = useContext(SpringContext)

  const { data: studentProjectData, loading: projectsLoading } = useQuery(STUDENT_PROJECTS_QUERY, {
    variables: {
      id,
    },
  })

  const projects = useMemo(
    () =>
      (studentProjectData?.studentProjects || [])
        .map((prj) => {
          const { orderList } = prj
          const meOrdersList = orderList.find((ord) => ord.userId === userId)
          return { ...prj, order: meOrdersList ? meOrdersList.order : 0 }
        })
        .sort((a, b) => a.order - b.order),
    [studentProjectData, userId],
  )

  const [fetchGrads, { data: gradsOther, loading: gradsLoading }] = useLazyQuery(
    QUERY.GRADS_PAGE_QUERY,
    {
      variables: {
        where: {
          studentYear: 2025,
          activeAt_gt: '2024-12-31',
        },
        first: 30,
      },
    },
  )

  const handleScrollTop = () => {
    if (spring != null && spring !== undefined) {
      spring.setCurrentValue(0).setAtRest()
      spring.setEndValue(0)
      fetchGrads()
    }
  }

  useEffect(() => {
    // Directly access the image path from the potentially new studentData
    const newImage = studentData?.student?.image

    if (newImage && newImage !== '') {
      console.log(`Setting image for user ${id}: ${newImage}`) // Debug log
      setUserImage(encodeURI(newImage))
    } else {
      // Explicitly reset to placeholder if no image, or if data is loading/missing
      console.log(`Resetting image for user ${id} (no image found)`) // Debug log
      setUserImage('static/2025/phProfile.png')
    }

    // Depend on the 'id' from the URL and the specific image data path.
    // When 'id' changes, this effect *will* re-run.
    // It also re-runs if the image data itself changes after load.
  }, [id, studentData?.student?.image]) // Added id dependency

  useEffect(() => {
    if (spring != null && spring !== undefined) {
      spring.setCurrentValue(0).setAtRest()
      spring.setEndValue(0)
    }
  }, [spring, fetchGrads])

  useEffect(() => {
    if (userId) {
      fetchGrads()
    }
  }, [fetchGrads, userId])

  const otherGrads = useMemo(() => {
    if (gradsOther?.users) {
      const grads = gradsOther?.users.map(
        (grad) => grad.id !== undefined && grad.id !== userId && grad,
      )
      if (grads?.length === 1 && grads[0] === false) {
        return []
      }
      return take(shuffle(grads), 3)
    }
    return []
  }, [gradsOther, userId])

  const backgroundColors = {
    header: isDarkMode ? 'bg-black' : 'bg-white',
    content: isDarkMode ? 'bg-dark-grey' : 'bg-light-grey-25',
    projects: isDarkMode ? 'bg-black' : 'bg-white',
    graduates: isDarkMode ? 'bg-dark-grey' : 'bg-light-grey-25',
    footer: isDarkMode ? 'bg-black' : 'bg-light-grey-25',
  }

  const textColors = {
    heading: isDarkMode ? 'text-lime' : 'text-lavender',
    subheading: isDarkMode ? 'text-white' : 'text-black',
    body: isDarkMode ? 'text-white' : 'text-black',
    link: isDarkMode ? 'text-lime hover:text-white' : 'text-lavender hover:text-black',
    accent: isDarkMode ? 'text-lime' : 'text-lavender',
  }

  const loading = studentLoading || projectsLoading || gradsLoading

  require('./profile.scss')
  return (
    <div
      className={`absolute w-full min-h-screen ${backgroundColors.header} flex flex-col items-center justify-start pb-20 lg:pb-0`}
    >
      {loading && <FullScreenLoader color={isDarkMode ? '#DAFF01' : '#8A74D0'} />}
      {Header && <Header />}

      <section className={`w-full ${backgroundColors.content} transition-colors duration-300`}>
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-32'>
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-16'>
            <div className='flex flex-col items-center lg:items-start'>
              <div className='relative overflow-hidden rounded-lg w-full max-w-[320px]'>
                <img
                  ref={imageRef}
                  src={userImage}
                  onError={() => setUserImage('static/2025/phProfile.png')}
                  className='object-cover w-full aspect-square rounded-lg shadow-md'
                  alt={`${user?.firstName} ${user?.lastName}`}
                />
              </div>
            </div>

            <div className='lg:col-span-2 mt-8 lg:mt-0'>
              {user?.portfolio && (
                <a
                  href={user?.portfolio}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={`${textColors.link} mt-6`}
                  style={{
                    paddingBottom: '8px',
                  }}
                >
                  <a
                    className={`${
                      isDarkMode ? 'text-white' : 'text-lavender'
                    } link inline-block dotted-text-link theme-transition`}
                    href={user?.portfolio}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Portfolio ↗
                  </a>
                </a>
              )}
              <h2 className={`${textColors.heading} heading-2`}>
                {user?.firstName} {user?.lastName}
              </h2>
              <p className={`${textColors.body} body-text mt-2`}>{user?.tagline}</p>

              <div className='flex flex-wrap gap-4 mt-4'>
                <p className={`${textColors.body} body-text`}>
                  <span className={`${textColors.heading} body-text`}>◼</span> {user?.program}
                </p>
                {disciplines[projectTypeMap[user?.primaryExpertise]] && (
                  <p className={`${textColors.body} body-text`}>
                    ◼ {disciplines[projectTypeMap[user?.primaryExpertise]]}
                  </p>
                )}
                {disciplines[projectTypeMap[user?.secondaryExpertise]] &&
                  disciplines[projectTypeMap[user?.primaryExpertise]] !==
                    disciplines[projectTypeMap[user?.secondaryExpertise]] && (
                    <p className={`${textColors.body} body-text`}>
                      ◼ {disciplines[projectTypeMap[user?.secondaryExpertise]]}
                    </p>
                  )}
              </div>

              <p className={`${textColors.body} body-text mt-6`}>{user?.about}</p>
              <div className='flex mt-6 gap-4'>
                {user.instagram && (
                  <a
                    href={user.instagram}
                    target='_blank'
                    rel='noreferrer'
                    className='hover:opacity-80 transition-opacity'
                  >
                    {isDarkMode ? (
                      <svg
                        width='56'
                        height='57'
                        viewBox='0 0 56 57'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='hover:scale-[1.04] transition-transform duration-300'
                      >
                        <circle cx='28' cy='28.3184' r='28' fill='#DAFF01' />
                        <path
                          d='M29.5419 13.3184C31.2294 13.3229 32.0859 13.3319 32.8254 13.3529L33.1164 13.3634C33.4524 13.3754 33.7839 13.3904 34.1844 13.4084C35.7804 13.4834 36.8694 13.7354 37.8249 14.1059C38.8149 14.4869 39.6489 15.0029 40.4829 15.8354C41.2456 16.5852 41.8358 17.4922 42.2124 18.4934C42.5829 19.4489 42.8349 20.5379 42.9099 22.1354C42.9279 22.5344 42.9429 22.8659 42.9549 23.2034L42.9639 23.4944C42.9864 24.2324 42.9954 25.0889 42.9984 26.7764L42.9999 27.8954V29.8604C43.0036 30.9545 42.9921 32.0486 42.9654 33.1424L42.9564 33.4334C42.9444 33.7709 42.9294 34.1024 42.9114 34.5014C42.8364 36.0989 42.5814 37.1864 42.2124 38.1434C41.8369 39.145 41.2466 40.0523 40.4829 40.8014C39.7328 41.5638 38.8259 42.154 37.8249 42.5309C36.8694 42.9014 35.7804 43.1534 34.1844 43.2284C33.8285 43.2451 33.4725 43.2601 33.1164 43.2734L32.8254 43.2824C32.0859 43.3034 31.2294 43.3139 29.5419 43.3169L28.4229 43.3184H26.4594C25.3648 43.3221 24.2702 43.3106 23.1759 43.2839L22.8849 43.2749C22.5288 43.2614 22.1728 43.2459 21.8169 43.2284C20.2209 43.1534 19.1319 42.9014 18.1749 42.5309C17.1739 42.1549 16.2672 41.5646 15.5184 40.8014C14.755 40.0518 14.1642 39.1447 13.7874 38.1434C13.4169 37.1879 13.1649 36.0989 13.0899 34.5014C13.0732 34.1454 13.0582 33.7894 13.0449 33.4334L13.0374 33.1424C13.0097 32.0486 12.9972 30.9545 12.9999 29.8604V26.7764C12.9957 25.6823 13.0067 24.5882 13.0329 23.4944L13.0434 23.2034C13.0554 22.8659 13.0704 22.5344 13.0884 22.1354C13.1634 20.5379 13.4154 19.4504 13.7859 18.4934C14.1626 17.4912 14.7545 16.5839 15.5199 15.8354C16.2685 15.0726 17.1746 14.4823 18.1749 14.1059C19.1319 13.7354 20.2194 13.4834 21.8169 13.4084C22.2159 13.3904 22.5489 13.3754 22.8849 13.3634L23.1759 13.3544C24.2697 13.3277 25.3638 13.3162 26.4579 13.3199L29.5419 13.3184ZM27.9999 20.8184C26.0108 20.8184 24.1031 21.6085 22.6966 23.0151C21.2901 24.4216 20.4999 26.3292 20.4999 28.3184C20.4999 30.3075 21.2901 32.2151 22.6966 33.6217C24.1031 35.0282 26.0108 35.8184 27.9999 35.8184C29.989 35.8184 31.8967 35.0282 33.3032 33.6217C34.7097 32.2151 35.4999 30.3075 35.4999 28.3184C35.4999 26.3292 34.7097 24.4216 33.3032 23.0151C31.8967 21.6085 29.989 20.8184 27.9999 20.8184ZM27.9999 23.8184C28.5908 23.8183 29.176 23.9346 29.722 24.1606C30.268 24.3867 30.7642 24.7181 31.1821 25.1358C31.6 25.5536 31.9316 26.0497 32.1578 26.5956C32.384 27.1415 32.5005 27.7267 32.5006 28.3176C32.5007 28.9086 32.3844 29.4937 32.1584 30.0397C31.9323 30.5857 31.6009 31.0819 31.1832 31.4998C30.7654 31.9177 30.2693 32.2493 29.7234 32.4755C29.1775 32.7018 28.5923 32.8183 28.0014 32.8184C26.8079 32.8184 25.6633 32.3443 24.8194 31.5003C23.9755 30.6564 23.5014 29.5118 23.5014 28.3184C23.5014 27.1249 23.9755 25.9803 24.8194 25.1364C25.6633 24.2925 26.8079 23.8184 28.0014 23.8184M35.8764 18.5684C35.3791 18.5684 34.9022 18.7659 34.5506 19.1175C34.1989 19.4692 34.0014 19.9461 34.0014 20.4434C34.0014 20.9406 34.1989 21.4176 34.5506 21.7692C34.9022 22.1208 35.3791 22.3184 35.8764 22.3184C36.3737 22.3184 36.8506 22.1208 37.2022 21.7692C37.5538 21.4176 37.7514 20.9406 37.7514 20.4434C37.7514 19.9461 37.5538 19.4692 37.2022 19.1175C36.8506 18.7659 36.3737 18.5684 35.8764 18.5684Z'
                          fill='#000000'
                        />
                      </svg>
                    ) : (
                      <svg
                        width='56'
                        height='57'
                        viewBox='0 0 56 57'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='hover:scale-[1.04] transition-transform duration-300'
                      >
                        <circle cx='28' cy='28.3184' r='28' fill='#9167F0' />
                        <path
                          d='M29.5419 13.3184C31.2294 13.3229 32.0859 13.3319 32.8254 13.3529L33.1164 13.3634C33.4524 13.3754 33.7839 13.3904 34.1844 13.4084C35.7804 13.4834 36.8694 13.7354 37.8249 14.1059C38.8149 14.4869 39.6489 15.0029 40.4829 15.8354C41.2456 16.5852 41.8358 17.4922 42.2124 18.4934C42.5829 19.4489 42.8349 20.5379 42.9099 22.1354C42.9279 22.5344 42.9429 22.8659 42.9549 23.2034L42.9639 23.4944C42.9864 24.2324 42.9954 25.0889 42.9984 26.7764L42.9999 27.8954V29.8604C43.0036 30.9545 42.9921 32.0486 42.9654 33.1424L42.9564 33.4334C42.9444 33.7709 42.9294 34.1024 42.9114 34.5014C42.8364 36.0989 42.5814 37.1864 42.2124 38.1434C41.8369 39.145 41.2466 40.0523 40.4829 40.8014C39.7328 41.5638 38.8259 42.154 37.8249 42.5309C36.8694 42.9014 35.7804 43.1534 34.1844 43.2284C33.8285 43.2451 33.4725 43.2601 33.1164 43.2734L32.8254 43.2824C32.0859 43.3034 31.2294 43.3139 29.5419 43.3169L28.4229 43.3184H26.4594C25.3648 43.3221 24.2702 43.3106 23.1759 43.2839L22.8849 43.2749C22.5288 43.2614 22.1728 43.2459 21.8169 43.2284C20.2209 43.1534 19.1319 42.9014 18.1749 42.5309C17.1739 42.1549 16.2672 41.5646 15.5184 40.8014C14.755 40.0518 14.1642 39.1447 13.7874 38.1434C13.4169 37.1879 13.1649 36.0989 13.0899 34.5014C13.0732 34.1454 13.0582 33.7894 13.0449 33.4334L13.0374 33.1424C13.0097 32.0486 12.9972 30.9545 12.9999 29.8604V26.7764C12.9957 25.6823 13.0067 24.5882 13.0329 23.4944L13.0434 23.2034C13.0554 22.8659 13.0704 22.5344 13.0884 22.1354C13.1634 20.5379 13.4154 19.4504 13.7859 18.4934C14.1626 17.4912 14.7545 16.5839 15.5199 15.8354C16.2685 15.0726 17.1746 14.4823 18.1749 14.1059C19.1319 13.7354 20.2194 13.4834 21.8169 13.4084C22.2159 13.3904 22.5489 13.3754 22.8849 13.3634L23.1759 13.3544C24.2697 13.3277 25.3638 13.3162 26.4579 13.3199L29.5419 13.3184ZM27.9999 20.8184C26.0108 20.8184 24.1031 21.6085 22.6966 23.0151C21.2901 24.4216 20.4999 26.3292 20.4999 28.3184C20.4999 30.3075 21.2901 32.2151 22.6966 33.6217C24.1031 35.0282 26.0108 35.8184 27.9999 35.8184C29.989 35.8184 31.8967 35.0282 33.3032 33.6217C34.7097 32.2151 35.4999 30.3075 35.4999 28.3184C35.4999 26.3292 34.7097 24.4216 33.3032 23.0151C31.8967 21.6085 29.989 20.8184 27.9999 20.8184ZM27.9999 23.8184C28.5908 23.8183 29.176 23.9346 29.722 24.1606C30.268 24.3867 30.7642 24.7181 31.1821 25.1358C31.6 25.5536 31.9316 26.0497 32.1578 26.5956C32.384 27.1415 32.5005 27.7267 32.5006 28.3176C32.5007 28.9086 32.3844 29.4937 32.1584 30.0397C31.9323 30.5857 31.6009 31.0819 31.1832 31.4998C30.7654 31.9177 30.2693 32.2493 29.7234 32.4755C29.1775 32.7018 28.5923 32.8183 28.0014 32.8184C26.8079 32.8184 25.6633 32.3443 24.8194 31.5003C23.9755 30.6564 23.5014 29.5118 23.5014 28.3184C23.5014 27.1249 23.9755 25.9803 24.8194 25.1364C25.6633 24.2925 26.8079 23.8184 28.0014 23.8184M35.8764 18.5684C35.3791 18.5684 34.9022 18.7659 34.5506 19.1175C34.1989 19.4692 34.0014 19.9461 34.0014 20.4434C34.0014 20.9406 34.1989 21.4176 34.5506 21.7692C34.9022 22.1208 35.3791 22.3184 35.8764 22.3184C36.3737 22.3184 36.8506 22.1208 37.2022 21.7692C37.5538 21.4176 37.7514 20.9406 37.7514 20.4434C37.7514 19.9461 37.5538 19.4692 37.2022 19.1175C36.8506 18.7659 36.3737 18.5684 35.8764 18.5684Z'
                          fill='#ffffff'
                        />
                      </svg>
                    )}
                  </a>
                )}
                {user.dribble && (
                  <a
                    href={user.dribble}
                    target='_blank'
                    rel='noreferrer'
                    className='hover:opacity-80 transition-opacity'
                  >
                    {isDarkMode ? (
                      <svg
                        width='60'
                        height='61'
                        viewBox='0 0 60 61'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='hover:scale-[1.04] transition-transform duration-300'
                      >
                        <circle cx='30' cy='30.3184' r='30' fill='#DAFF01' />
                        <path
                          d='M36.9002 21.0484C35.8749 19.8778 35.3098 18.3745 35.3102 16.8184H30.6752V35.4184C30.6395 36.4249 30.2145 37.3783 29.4899 38.0778C28.7652 38.7772 27.7974 39.1682 26.7902 39.1684C24.6602 39.1684 22.8902 37.4284 22.8902 35.2684C22.8902 32.6884 25.3802 30.7534 27.9452 31.5484V26.8084C22.7702 26.1184 18.2402 30.1384 18.2402 35.2684C18.2402 40.2634 22.3802 43.8184 26.7752 43.8184C31.4852 43.8184 35.3102 39.9934 35.3102 35.2684V25.8334C37.1897 27.1831 39.4463 27.9073 41.7602 27.9034V23.2684C41.7602 23.2684 38.9402 23.4034 36.9002 21.0484Z'
                          fill='#000000'
                        />
                      </svg>
                    ) : (
                      <svg
                        width='60'
                        height='61'
                        viewBox='0 0 60 61'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='hover:scale-[1.04] transition-transform duration-300'
                      >
                        <circle cx='30' cy='30.3184' r='30' fill='#9167F0' />
                        <path
                          d='M36.9002 21.0484C35.8749 19.8778 35.3098 18.3745 35.3102 16.8184H30.6752V35.4184C30.6395 36.4249 30.2145 37.3783 29.4899 38.0778C28.7652 38.7772 27.7974 39.1682 26.7902 39.1684C24.6602 39.1684 22.8902 37.4284 22.8902 35.2684C22.8902 32.6884 25.3802 30.7534 27.9452 31.5484V26.8084C22.7702 26.1184 18.2402 30.1384 18.2402 35.2684C18.2402 40.2634 22.3802 43.8184 26.7752 43.8184C31.4852 43.8184 35.3102 39.9934 35.3102 35.2684V25.8334C37.1897 27.1831 39.4463 27.9073 41.7602 27.9034V23.2684C41.7602 23.2684 38.9402 23.4034 36.9002 21.0484Z'
                          fill='#ffffff'
                        />
                      </svg>
                    )}
                  </a>
                )}
                {user.linkedIn && (
                  <a
                    href={user.linkedIn}
                    target='_blank'
                    rel='noreferrer'
                    className='hover:opacity-80 transition-opacity'
                  >
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z'
                        fill={isDarkMode ? '#DAFF01' : '#8A74D0'}
                      />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`w-full ${backgroundColors.projects} transition-colors duration-300`}>
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16'>
          <h2 className={`${textColors.heading} heading-2 mb-12`}>PROJECTS</h2>

          <div className='flex flex-col gap-16'>
            {projects.map((project) => (
              <div key={project.id} className='grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16'>
                <Link to={{ pathname: '/project', search: `id=${project.id}` }} className='block'>
                  <div className='overflow-hidden rounded-lg shadow-md'>
                    <img
                      src={project.coverImage}
                      alt={project.title}
                      className='w-full h-full object-cover transition-transform duration-300 hover:scale-105'
                    />
                  </div>
                </Link>
                <div className='flex flex-col'>
                  <Link to={{ pathname: '/project', search: `id=${project.id}` }}>
                    <h3 className={`${textColors.subheading} heading-3`}>{project.title}</h3>
                  </Link>

                  <div className='flex flex-wrap gap-4 mt-4'>
                    <Link
                      to={{
                        pathname: '/work',
                        search: `?program=${user?.program}`,
                      }}
                    >
                      <p className={`${textColors.body} body-text`}>
                        <span className={`${textColors.heading} body-text`}>◼</span> {user?.program}
                      </p>
                    </Link>

                    <Link
                      className='cursor-pointer'
                      to={{
                        pathname: '/work',
                        search: `?type=${project['primaryProjectType']}`,
                      }}
                    >
                      {disciplines[project['primaryProjectType']] && (
                        <p className={`${textColors.body} body-text`}>
                          ◼ {disciplines[project['primaryProjectType']]}
                        </p>
                      )}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`w-full ${backgroundColors.graduates} transition-colors duration-300`}>
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16'>
          <h2 className={`${textColors.heading} heading-2 mb-12`}>OTHER GRADUATES</h2>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {gradsOther?.users &&
              otherGrads.length > 0 &&
              otherGrads.map((_grad, idx) => (
                <GradCard
                  key={idx}
                  grad={_grad}
                  scrollTop={handleScrollTop}
                  isDarkMode={isDarkMode}
                />
              ))}
          </div>
        </div>
      </section>

      <div
        className={`relative w-full ${backgroundColors.footer} flex flex-col items-center justify-center w-full`}
      >
        <div className='relative bottom-0 w-full pt-16 px-[16px] lg:px-[48px] max-w-[1440px]'>
          {Footer && <Footer />}
        </div>
      </div>
    </div>
  )
}

const SINGLE_USER_QUERY = gql`
  query SINGLE_USER_QUERY($id: ID!) {
    student(where: { id: $id }) {
      id
      email
      firstName
      lastName
      tagline
      program
      contactEmail
      primaryExpertise
      secondaryExpertise
      about
      image
      portfolio
      behance
      linkedIn
      instagram
      twitter
      facebook
      dribble
      youtube
      vimeo
      medium
    }
  }
`

const STUDENT_PROJECTS_QUERY = gql`
  query STUDENT_PROJECTS_QUERY($id: ID!) {
    studentProjects(where: { id: $id }) {
      id
      title
      primaryProjectType
      secondaryProjectType
      secondaryProjectType_2
      contentType
      contentData
      coverImage
      orderList {
        userId
        order
      }
    }
  }
`
